import { useMemo } from 'react';
import { useApi } from './useApi';

export interface AdminMetrics {
  totalParents: number;
  paidParents: number;
  freeParents: number;
  statsParents: number;
  reportsParents: number;
  mrrPence: number;
  mrrFormatted: string;
  suspendedCount: number;
  archivedCount: number;
  signupsToday: number;
  signupsThisWeek: number;
  signupsThisMonth: number;
  paymentFailedCount: number;
  dormantCount: number;
  truncated: boolean;
  generatedAt: string;
}

export interface AdminParentRow {
  parentId: string;
  email: string;
  displayName?: string;
  plan: 'free' | 'stats' | 'reports';
  status: 'active' | 'suspended';
  archived: boolean;
  kidsCount: number;
  matchesTotal: number;
  matchSendCount: number;
  storageBytes: number;
  photoCount: number;
  voiceNoteCount: number;
  paymentFailedAt?: string;
  suspendedAt?: string;
  createdAt: string;
  lastLoginAt?: string;
}

export interface AdminKidUsage {
  kidId: string;
  name?: string;
  surname?: string;
  dob?: string;
  clubName?: string;
  photos: number;
  voiceNotes: number;
  profilePhotos: number;
  bytes: number;
  deletedAt?: string;
}

export interface AdminParentDetail extends AdminParentRow {
  matchesUsedByYear?: Record<string, number>;
  malwareUploadCount?: number;
  lastMalwareUploadAt?: string;
  matchSendCountDay?: string;
  dormancyWarn1At?: string;
  dormancyWarn2At?: string;
  dormancyWarn3At?: string;
  archivedAt?: string;
  suspendedReason?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePriceId?: string;
  hadReportsSubscriptionAt?: string;
  paymentFailedReason?: string;
  trialEndsAt?: string;
  sendToFamilyTotalCount?: number;
  suspensionHistory?: Array<{ at: string; by: string; byEmail?: string; action: string; reason: string }>;
  kids?: AdminKidUsage[];
  storage?: {
    totalBytes: number;
    totalObjects: number;
    matchPhotos: number;
    voiceNotes: number;
    kidProfilePhotos: number;
  } | null;
}

export interface FailedPaymentRow {
  parentId: string;
  email: string;
  plan: string;
  paymentFailedAt: string;
  paymentFailedReason?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

export interface DormancyRow {
  parentId: string;
  email: string;
  plan: string;
  stage: 1 | 2 | 3;
  dormancyWarn1At?: string;
  dormancyWarn2At?: string;
  dormancyWarn3At?: string;
  lastLoginAt?: string;
  archived: boolean;
}

export function useAdminApi() {
  const call = useApi();
  return useMemo(() => ({
    metrics: () => call<AdminMetrics>('/api/v1/admin/metrics'),
    listParents: (q?: { plan?: string; status?: string; search?: string }) => {
      const params = new URLSearchParams();
      if (q?.plan) params.set('plan', q.plan);
      if (q?.status) params.set('status', q.status);
      if (q?.search) params.set('q', q.search);
      const qs = params.toString();
      return call<{ total: number; truncated: boolean; items: AdminParentRow[] }>(
        `/api/v1/admin/parents${qs ? `?${qs}` : ''}`,
      );
    },
    getParent: (parentId: string) =>
      call<AdminParentDetail>(`/api/v1/admin/parent/${parentId}`),
    failedPayments: () =>
      call<{ total: number; items: FailedPaymentRow[] }>('/api/v1/admin/failed-payments'),
    dormancy: () =>
      call<{ total: number; items: DormancyRow[] }>('/api/v1/admin/dormancy-queue'),
    suspend: (parentId: string, reason: string) =>
      call<{ ok: true; parentId: string; status: 'suspended'; suspendedAt: string; stripe: string }>(
        `/api/v1/admin/parent/${parentId}/suspend`,
        { method: 'POST', body: { reason } },
      ),
    unsuspend: (parentId: string, reason?: string) =>
      call<{ ok: true; parentId: string; status: 'active'; unsuspendedAt: string; stripe: string }>(
        `/api/v1/admin/parent/${parentId}/unsuspend`,
        { method: 'POST', body: { reason: reason ?? '' } },
      ),
  }), [call]);
}
